import { useEffect, useState } from 'react';
import { CircularProgress, Grid, TextField, Typography } from '@material-ui/core';
import { PHONE_REGEX } from 'config/constants';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import cniLogo from '_assets/parameters/cni-logo.svg';
import DefaultButton from 'components/DefaultButton';
import DefaultMenuItem from 'components/DefaultMenuItem';
import DocumentCard from 'components/DocumentCard';
import useStyles from './styles';

const validationSchema = Yup.object({
  birthday: Yup.string().required('Champ obligatoire'),
  birthplace: Yup.string().required('Champ obligatoire'),
  country: Yup.string().required('Champ obligatoire'),
  gender: Yup.string().required('Champ obligatoire'),
  firstName: Yup.string().required('Champ obligatoire'),
  lastName: Yup.string().required('Champ obligatoire'),
  phone: Yup.string()
    .matches(PHONE_REGEX, 'Veuillez saisir un numéro de téléphone valide')
    .required('Champ obligatoire'),
});

function IdentityForm({ disabled, identity, onCancel = () => {}, onSubmit = () => {} }) {
  const classes = useStyles();

  const [errorDoc, setErrorDoc] = useState({ onError: false, onWarning: false, message: null });

  const {
    errors,
    handleChange,
    handleSubmit,
    isSubmitting,
    resetForm,
    touched,
    values,
  } = useFormik({
    initialValues: { ...identity, country: identity.country?.length !==0 ? identity.country : 'France' },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (!document.file || errorDoc.onError) return;

      const { documentUpdated } = await onSubmit(values, { ...document });
      setDocument({
        file: null,
        url: documentUpdated ? document.url : identity?.identityURL ?? null,
      });
    },
  });

  const [document, setDocument] = useState({ file: null, url: identity?.identityURL ?? null });

  useEffect(() => {
    if(isSubmitting){
      if (!document.file || errorDoc.onError) {
        setErrorDoc({ onError: true, message: 'Une pièce d\'identité valide est nécessaire.' });
      }
    }
    // eslint-disable-next-line
  }, [isSubmitting]);

  const handleDocumentChange = (file, url) => {
    if (file?.type.endsWith('pdf')) {
      setErrorDoc( { onError: false, onWarning: false, message: null });
      setDocument({ file, url });
    } else if(document.file?.type.endsWith('pdf')) {
      setErrorDoc({onError: false, onWarning: true, message: 'Le fichier doit être au format PDF.'});
    } else {
      setErrorDoc({ onError: true, onWarning: false, message: 'Le fichier doit être au format PDF.' });
    }
  };

  const onCancelFormClick = () => {
    if (isSubmitting) return;

    onCancel();
    resetForm();
    setDocument({ file: null, url: identity?.identityURL ?? null });
  };

  return (
    <form
      autoComplete='off'
      className={classes.root}
      onSubmit={handleSubmit}
    >
      <Grid container justifyContent='center' style={{ height: '100%' }}>
        <Grid container item xs={12} md={9} alignItems='center' justifyContent='center' direction='column' spacing={3} style={{ flexGrow: 1 }}>
          <Grid container item>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <TextField
                  disabled={disabled}
                  fullWidth
                  label="Prénom"
                  name="firstName"
                  variant="outlined"
                  value={values.firstName}
                  onChange={handleChange}
                  error={touched.firstName && Boolean(errors.firstName)}
                  helperText={touched.firstName && errors.firstName}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  disabled={disabled}
                  fullWidth
                  label="Nom"
                  name="lastName"
                  variant="outlined"
                  value={values.lastName}
                  onChange={handleChange}
                  error={touched.lastName && Boolean(errors.lastName)}
                  helperText={touched.lastName && errors.lastName}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid container item>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <TextField
                  disabled={disabled}
                  fullWidth
                  label="Téléphone"
                  name="phone"
                  variant="outlined"
                  value={values.phone}
                  onChange={handleChange}
                  error={touched.phone && Boolean(errors.phone)}
                  helperText={touched.phone && errors.phone}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  disabled={disabled}
                  fullWidth
                  label="Date de naissance"
                  name="birthday"
                  type="date"
                  variant="outlined"
                  value={values.birthday}
                  onChange={handleChange}
                  error={touched.birthday && Boolean(errors.birthday)}
                  helperText={touched.birthday && errors.birthday}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid container item>
            <Grid container spacing={3}>
              <Grid item xs={3}>
                <TextField
                  disabled={disabled}
                  fullWidth
                  select
                  label="Genre"
                  name="gender"
                  variant="outlined"
                  value={values.gender}
                  onChange={handleChange}
                  error={touched.gender && Boolean(errors.gender)}
                  helperText={touched.gender && errors.gender}
                >
                  <DefaultMenuItem value="M">Homme</DefaultMenuItem>
                  <DefaultMenuItem value="F">Femme</DefaultMenuItem>
                </TextField>
              </Grid>

              <Grid item xs={4}>
                <TextField
                  disabled={disabled}
                  fullWidth
                  label="Ville de naissance"
                  name="birthplace"
                  variant="outlined"
                  value={values.birthplace}
                  onChange={handleChange}
                  error={touched.birthplace && Boolean(errors.birthplace)}
                  helperText={touched.birthplace && errors.birthplace}
                />
              </Grid>

            <Grid item xs={5}>
              <TextField
                disabled={disabled}
                fullWidth
                label="Pays"
                name="country"
                variant="outlined"
                value={values.country?.length > 0 ? values.country : 'France'}
                onChange={handleChange}
                error={touched.country && Boolean(errors.country)}
                helperText={touched.country && errors.country}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'center', width: '50%' }}>
          <DocumentCard
            disabled={disabled}
            document={document}
            logo={cniLogo}
            name="Carte d'identité"
            onChange={handleDocumentChange}
          />
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        { (errorDoc.onError || errorDoc.onWarning) &&
          <Typography color='error'>
            {errorDoc.message}
          </Typography>
        }
      </div>

        {!disabled && (
          <Grid container item xs={12} justifyContent='center'>
            <Grid container item xs={12} alignItems='flex-end' justifyContent='center' style={{ marginBottom: '16px' }}>
              <DefaultButton cancel onClick={onCancelFormClick}>
                Annuler
              </DefaultButton>
              <DefaultButton
                color='secondary'
                disabled={isSubmitting}
                variant='contained'
                type='submit'
              >
                {isSubmitting ? <CircularProgress size={20} /> : 'Valider'}
              </DefaultButton>
            </Grid>
          </Grid>
        )}
      </Grid>
    </form>
  );
}

export default IdentityForm;
